import { useForm } from "@tanstack/react-form";
import {
	createContext,
	type ReactNode,
	useCallback,
	useContext,
	useEffect,
	useMemo,
	useRef,
	useState,
} from "react";
import { buildLayoutFieldTypeIndex } from "@/lib/layoutRenderer/utils/layout-field-type-index.util";
import type {
	DynamicFormSchema,
	FieldValidation,
	FormField,
	FormValue,
} from "../interfaces/dynamicForm.interface";
import { mergeFormDefaultValues } from "../utils/merge-form-default-values.util";
import { persistedDefinitionToFormField } from "../utils/persisted-field-definition.util";
import type { DynamicFormRequirementMode } from "../utils/requirement-mode.util";
import {
	buildRuleIndexes,
	evaluateFieldRules,
	getDependentFields,
} from "../utils/ruleEvaluator.util";

// Field state interface
interface FieldState {
	shouldShow: boolean | undefined;
	shouldEnable: boolean | undefined;
	isRequired: boolean | undefined;
	validation?: FieldValidation;
}

// Context interface
interface DynamicFormContextType {
	schema: DynamicFormSchema;
	form: any; // Using any to avoid complex type issues with TanStack Form
	fieldStates: Record<string, FieldState>;
	formValues: Record<string, unknown>;
	handleFieldChange: (
		fieldName: string,
		value: unknown,
		meta?: { isValid?: boolean },
	) => void;
	/** Notify parent that a field left focus (persist continuous fields). */
	handleFieldBlur: (fieldName: string) => void;
	/** Snapshot value when a continuous field gains focus (Escape restore baseline). */
	handleFieldFocus: (fieldName: string, value: unknown) => void;
	/** Restore focus baseline and notify parent with phase cancel (no persist). */
	handleFieldCancel: (fieldName: string) => void;
	editorMode?: boolean;
	/** When true, form is embedded for UI preview only (suppress noisy dev behavior). */
	previewMode?: boolean;
	requirementMode: DynamicFormRequirementMode;
}

// Create context
const DynamicFormContext = createContext<DynamicFormContextType | undefined>(
	undefined,
);

// Hook to use the context
export const useDynamicFormContext = () => {
	const context = useContext(DynamicFormContext);
	if (!context) {
		// throw new Error('useDynamicFormContext must be used within a DynamicFormProvider');
		console.error(
			"useDynamicFormContext must be used within a DynamicFormProvider",
		);
		return undefined;
	}
	return context;
};

// Provider props interface
interface DynamicFormProviderProps {
	schema: DynamicFormSchema;
	editorMode?: boolean;
	previewMode?: boolean;
	requirementMode?: DynamicFormRequirementMode;
	children: ReactNode;
	onSubmit?: (value: FormValue) => void;
	onFieldStateChange?: (fieldStates: Record<string, FieldState>) => void;
	initialValues?: Record<string, unknown>;
	onValuesChange?: (
		values: Record<string, unknown>,
		meta: {
			changedField?: string;
			isValid?: boolean;
			phase?: "change" | "blur" | "cancel";
			fieldType?: string;
		},
	) => void;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function applyFormFieldDefault(
	field: FormField,
	defaults: Record<string, any>,
) {
	switch (field.type) {
		case "text":
		case "phone":
		case "url":
			defaults[field.name] = "";
			break;
		case "number":
			defaults[field.name] = field.validation?.min ?? 0;
			break;
		case "checkbox":
			defaults[field.name] = false;
			break;
		case "switch":
			defaults[field.name] = false;
			break;
		case "radio":
		case "select":
			defaults[field.name] = field.defaultValue ?? "";
			break;
		case "slider":
			defaults[field.name] = field.defaultValue ?? field.min;
			break;
		case "toggleButton":
			defaults[field.name] = field.exclusive ? (field.defaultValue ?? "") : [];
			break;
		case "autocomplete":
			defaults[field.name] = field.multiple ? [] : (field.defaultValue ?? "");
			break;
		case "date":
		case "time":
		case "dateTime":
			defaults[field.name] = field.defaultValue ?? "";
			break;
		case "file":
			defaults[field.name] = null;
			break;
		case "multiSelect":
		case "users":
		case "signature":
			defaults[field.name] = null;
			break;
		case "address":
			defaults[field.name] = "";
			break;
		case "group":
			for (const child of field.fields) {
				applyFormFieldDefault(child, defaults);
			}
			break;
		case "fieldArray":
			defaults[field.name] = [];
			break;
		default:
			defaults[field.name] = "";
	}
}

// Helper function to generate default values from schema
// eslint-disable-next-line @typescript-eslint/no-explicit-any
function generateDefaultValues(schema: DynamicFormSchema): Record<string, any> {
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	const defaults: Record<string, any> = {};
	const layoutFieldTypeByDefinitionKey = buildLayoutFieldTypeIndex(
		schema.layoutJson,
	);

	for (const def of schema.fieldDefinitions) {
		const field = persistedDefinitionToFormField(def, {
			layoutFieldTypeByDefinitionKey,
		});
		if (!field) continue;
		applyFormFieldDefault(field, defaults);
	}

	return defaults;
}

// Helper function to get all fields including nested group fields
function getAllFields(fields: FormField[]): FormField[] {
	const allFields: FormField[] = [];

	for (const field of fields) {
		allFields.push(field);
		if (field.type === "group") {
			allFields.push(...getAllFields(field.fields));
		}
		if (field.type === "fieldArray") {
			allFields.push(...getAllFields(field.fields));
		}
	}

	return allFields;
}

export function DynamicFormProvider({
	schema,
	editorMode,
	previewMode,
	requirementMode = "require",
	children,
	onSubmit,
	onFieldStateChange,
	initialValues,
	onValuesChange,
}: DynamicFormProviderProps) {
	const defaultValues = mergeFormDefaultValues(
		generateDefaultValues(schema),
		initialValues,
	);
	const [fieldStates, setFieldStates] = useState<Record<string, FieldState>>(
		{},
	);
	const [formValues, setFormValues] = useState(defaultValues);
	const formValuesRef = useRef(formValues);
	formValuesRef.current = formValues;
	const focusBaselineByFieldRef = useRef<Record<string, unknown>>({});
	const isCancellingRef = useRef(false);

	const allFields = useMemo(() => {
		const layoutFieldTypeByDefinitionKey = buildLayoutFieldTypeIndex(
			schema.layoutJson,
		);
		const flat: FormField[] = [];
		for (const def of schema.fieldDefinitions) {
			const f = persistedDefinitionToFormField(def, {
				layoutFieldTypeByDefinitionKey,
			});
			if (f) flat.push(...getAllFields([f]));
		}
		return flat;
	}, [schema.fieldDefinitions, schema.layoutJson]);

	const form = useForm({
		defaultValues,
		onSubmit: async ({ value }) => {
			alert("✅ VALIDATION PASSED! Moving to submission...");
			console.log("✅ TanStack Form validation passed!");
			console.log("Form values:", value);

			// Call the parent onSubmit handler which has Zod validation
			onSubmit?.(value);
		},
		onSubmitInvalid: ({
			value,
			formApi,
		}: {
			value: FormValue;
			formApi: any;
		}) => {
			alert("❌ VALIDATION FAILED! Checking errors...");
			console.log("❌ TanStack Form validation failed!");

			const fieldErrors: string[] = [];
			Object.entries(formApi.state.fieldMeta).forEach(
				([fieldName, meta]: [string, any]) => {
					if (meta.errors && meta.errors.length > 0) {
						fieldErrors.push(`${fieldName}: ${meta.errors.join(", ")}`);
					}
				},
			);

			if (fieldErrors.length > 0) {
				alert(
					`❌ Validation Failed!\n\nPlease fix the following errors:\n\n${fieldErrors.join("\n")}`,
				);
			} else {
				alert(
					"❌ Validation failed but no specific field errors found. Check console.",
				);
			}
			console.log("Field errors:", fieldErrors);
			console.log("Current form values:", value);
			console.log("Form state:", formApi.state);
		},
	});
	const defaultValuesSnapshot = JSON.stringify(defaultValues);

	useEffect(() => {
		if (JSON.stringify(formValuesRef.current) === defaultValuesSnapshot) return;
		const nextValues = JSON.parse(defaultValuesSnapshot) as Record<
			string,
			unknown
		>;
		form.reset(nextValues);
		formValuesRef.current = nextValues;
		setFormValues(nextValues);
	}, [defaultValuesSnapshot, form]);

	// Precompute lookup + dependency indexes once per (rules, fields) change.
	const ruleIndexes = useMemo(
		() => buildRuleIndexes(schema.rules, allFields),
		[schema.rules, allFields],
	);

	// Reverse lookup so handleFieldChange can map TanStack Form's `fieldName` back to the field id.
	const fieldIdByName = useMemo(() => {
		const map = new Map<string, string>();
		for (const f of allFields) map.set(f.name, f.id);
		return map;
	}, [allFields]);

	const fieldTypeByName = useMemo(() => {
		const map = new Map<string, string>();
		for (const f of allFields) map.set(f.name, f.type);
		return map;
	}, [allFields]);

	const evaluateRulesForField = useCallback(
		(field: FormField, currentValues: Record<string, unknown>) => {
			if (!field) return null;
			const ruleResult = evaluateFieldRules(field, ruleIndexes, currentValues);
			return {
				shouldShow: ruleResult.shouldShow,
				shouldEnable: ruleResult.shouldEnable,
				isRequired: ruleResult.isRequired,
				validation: ruleResult.validation,
			} as FieldState;
		},
		[ruleIndexes],
	);

	// Recompute rule-derived field states when definitions change (e.g. builder toggles `required`).
	useEffect(() => {
		const nextStates: Record<string, FieldState> = {};

		for (const field of allFields) {
			const result = evaluateRulesForField(field, formValuesRef.current);
			if (result) {
				nextStates[field.id] = result;
			}
		}

		setFieldStates(nextStates);
	}, [allFields, evaluateRulesForField]);

	// Re-evaluate only fields downstream of the changed source (plus the source itself).
	const handleFieldChange = useCallback(
		(fieldName: string, value: unknown, meta?: { isValid?: boolean }) => {
			// React only runs a setState updater during dispatch as a bail-out
			// optimization, so next values must be derived from the ref instead.
			const nextFormValues = {
				...formValuesRef.current,
				[fieldName]: value,
			};
			formValuesRef.current = nextFormValues;
			setFormValues(nextFormValues);

			const sourceFieldId = fieldIdByName.get(fieldName);
			if (sourceFieldId != null) {
				const affectedIds = getDependentFields(sourceFieldId, ruleIndexes);
				if (affectedIds.length > 0) {
					setFieldStates((prev) => {
						let next = prev;
						for (const id of affectedIds) {
							const field = ruleIndexes.fieldsByDefinitionId.get(id);
							if (!field) continue;
							const result = evaluateRulesForField(field, nextFormValues);
							if (!result) continue;
							if (next === prev) next = { ...prev };
							next[id] = result;
						}
						return next;
					});
				}
			}

			if (isCancellingRef.current) {
				return;
			}

			onValuesChange?.(nextFormValues, {
				changedField: fieldName,
				isValid: meta?.isValid,
				phase: "change",
				fieldType: fieldTypeByName.get(fieldName),
			});
		},
		[
			evaluateRulesForField,
			fieldIdByName,
			fieldTypeByName,
			onValuesChange,
			ruleIndexes,
		],
	);

	const handleFieldBlur = useCallback(
		(fieldName: string) => {
			onValuesChange?.(formValuesRef.current, {
				changedField: fieldName,
				phase: "blur",
				fieldType: fieldTypeByName.get(fieldName),
			});
		},
		[fieldTypeByName, onValuesChange],
	);

	const handleFieldFocus = useCallback((fieldName: string, value: unknown) => {
		focusBaselineByFieldRef.current[fieldName] = value;
	}, []);

	const applyValueAndRules = useCallback(
		(fieldName: string, value: unknown) => {
			const nextFormValues = {
				...formValuesRef.current,
				[fieldName]: value,
			};
			formValuesRef.current = nextFormValues;
			setFormValues(nextFormValues);

			const sourceFieldId = fieldIdByName.get(fieldName);
			if (sourceFieldId != null) {
				const affectedIds = getDependentFields(sourceFieldId, ruleIndexes);
				if (affectedIds.length > 0) {
					setFieldStates((prev) => {
						let next = prev;
						for (const id of affectedIds) {
							const field = ruleIndexes.fieldsByDefinitionId.get(id);
							if (!field) continue;
							const result = evaluateRulesForField(field, nextFormValues);
							if (!result) continue;
							if (next === prev) next = { ...prev };
							next[id] = result;
						}
						return next;
					});
				}
			}

			return nextFormValues;
		},
		[evaluateRulesForField, fieldIdByName, ruleIndexes],
	);

	const handleFieldCancel = useCallback(
		(fieldName: string) => {
			if (!Object.hasOwn(focusBaselineByFieldRef.current, fieldName)) {
				return;
			}
			const value = focusBaselineByFieldRef.current[fieldName];

			isCancellingRef.current = true;
			try {
				form.setFieldValue(fieldName, value);
				const nextFormValues = applyValueAndRules(fieldName, value);
				if (nextFormValues) {
					formValuesRef.current = nextFormValues;
					onValuesChange?.(nextFormValues, {
						changedField: fieldName,
						phase: "cancel",
						fieldType: fieldTypeByName.get(fieldName),
					});
				}
			} finally {
				isCancellingRef.current = false;
			}
		},
		[applyValueAndRules, fieldTypeByName, form, onValuesChange],
	);

	useEffect(() => {
		onFieldStateChange?.(fieldStates);
	}, [fieldStates, onFieldStateChange]);

	const contextValue: DynamicFormContextType = {
		schema,
		form,
		fieldStates,
		formValues,
		handleFieldChange,
		handleFieldBlur,
		handleFieldFocus,
		handleFieldCancel,
		editorMode,
		previewMode,
		requirementMode,
	};

	return (
		<DynamicFormContext.Provider value={contextValue}>
			{children}
		</DynamicFormContext.Provider>
	);
}
